import { Currency } from 'module/api/account/types';
import {
  IntegrationField,
  RLProjectFieldMapping,
  ProjectFinancialsContractType,
} from 'module/api/integrations/salesforce/types';
import { StatusTypes } from 'module/api/integrations/salesforce/types';
import { useTranslation } from 'module/hooks/use-translation';
import RlLogo from 'module/ui/assets/svgs/rocketlane_icon_standard.svg';
import SalesforceLogo from 'module/ui/assets/svgs/salesforce-logo.svg';
import { ButtonGroup } from 'module/ui/components/atoms/button';
import {
  CustomLayoutDrawer,
  CustomLayoutDrawerContent,
  DefaultCustomLayoutDrawerProps,
} from 'module/ui/components/atoms/drawer/custom-layout-drawer';
import { Form } from 'module/ui/components/atoms/form';
import {
  InlineNotification,
  InlineNotificationType,
} from 'module/ui/components/atoms/inline-notification';
import { Loading } from 'module/ui/components/atoms/loading';
import { CustomOption } from 'module/ui/components/atoms/select';
import { Control, Select } from 'module/ui/components/atoms/select';
import { Menu } from 'module/ui/components/atoms/select/components';
import { VirtualizedMenuList } from 'module/ui/components/molecules/grouped-dropdown/virtualised-list';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar, Button } from '@rocketlane/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Col, Row } from 'antd';
import { groupBy } from 'lodash';
import {
  SALESFORCE_AUTOMATION_BILLING_METHOD_FINANCIALS_FIELDS_QUERY_KEY,
  SALESFORCE_AUTOMATION_TEMPLATE_PLACEHOLDERS_FIELDS_QUERY_KEY,
} from 'queries/salesforce';
import { FC, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { components as RsComponents } from 'react-select';
import styled from 'styled-components';
import { useAutomationContext } from './automation-context';

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
  min-width: 0;
`;

const OptionMainText = styled.span`
  min-width: 0;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Used when we want the secondary meta text to be pushed to the right
const OptionMainTextGrow = styled(OptionMainText)`
  flex: 1;
`;

const OptionMetaText = styled.span`
  flex-shrink: 0;
  font-size: var(--font-size-sm);
  color: var(--text-tertiary);
`;

const OptionFieldIdPill = styled.span`
  flex-shrink: 0;
  max-width: calc(var(--spacing-03) * 25);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: var(--spacing-01) var(--spacing-03);
  border-radius: var(--border-radius-xsmall);
  background: var(--ozone-magenta-surface);
  color: var(--ozone-magenta);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-01);
  font-weight: var(--font-weight-semi-bold);
`;

const SelectedSingleValue = (props: any) => {
  const rawLabel = String(props?.data?.label ?? '');
  const optionValueStr = String(
    props?.data?.valueStr ?? props?.data?.value ?? '',
  );
  const selectName = String(props?.selectProps?.name ?? '');
  const isContactObjectField = selectName === 'contactObject';
  const shouldShowSalesforceLogo =
    isContactObjectField || optionValueStr.startsWith('${trigger.');

  return (
    <RsComponents.SingleValue {...props}>
      <OptionRow>
        <Avatar
          src={shouldShowSalesforceLogo ? SalesforceLogo : RlLogo}
          shape="square"
          size={20}
        />
        <OptionMainTextGrow title={rawLabel}>{rawLabel}</OptionMainTextGrow>
      </OptionRow>
    </RsComponents.SingleValue>
  );
};

const SelectedMultiValueLabel = (props: any) => {
  const rawLabel = String(props?.data?.label ?? '');
  const optionValueStr = String(
    props?.data?.valueStr ?? props?.data?.value ?? '',
  );
  const selectName = String(props?.selectProps?.name ?? '');
  const isContactObjectField = selectName === 'contactObject';
  const shouldShowSalesforceLogo =
    isContactObjectField || optionValueStr.startsWith('${trigger.');

  return (
    <RsComponents.MultiValueLabel {...props}>
      <OptionRow>
        <Avatar
          src={shouldShowSalesforceLogo ? SalesforceLogo : RlLogo}
          shape="square"
          size={20}
        />
        <OptionMainTextGrow title={rawLabel}>{rawLabel}</OptionMainTextGrow>
      </OptionRow>
    </RsComponents.MultiValueLabel>
  );
};

// DisplayDynamicValue component defined inline
const DisplayDynamicValue = (option: CustomOption) => {
  const rawLabel = String(option.label ?? '');
  const pillText = option.valueMeta?.pillText as string | undefined;
  const showPill = Boolean(pillText);

  return (
    <OptionRow>
      {showPill ? (
        <>
          <OptionMainText title={rawLabel}>{rawLabel}</OptionMainText>
          <OptionFieldIdPill title={pillText}>{pillText}</OptionFieldIdPill>
        </>
      ) : (
        <>
          <OptionMainTextGrow title={rawLabel}>{rawLabel}</OptionMainTextGrow>
          {option.valueMeta &&
            (option.valueMeta.description || option.valueMeta.type) && (
              <OptionMetaText>
                {option.valueMeta.description || option.valueMeta.type}
              </OptionMetaText>
            )}
        </>
      )}
    </OptionRow>
  );
};

type ActionDrawerProps = {
  fields: IntegrationField[];
  fieldsStatus: StatusTypes | boolean;
  visible: boolean;
  label: string;
  value?: RLProjectFieldMapping;
  handleModalVisibility: (visibility: boolean) => void;
  onSave: (actions: RLProjectFieldMapping) => void;
  onClose: () => void;
  placeholderFields?: IntegrationField[];
  placeholderStatus?: StatusTypes;
  financialFields?: IntegrationField[];
  isFinancialsFieldsFetching?: boolean;
  onTemplateChange?: (templateId: string | null) => void;
  onBillingMethodChange?: (
    billingMethod: ProjectFinancialsContractType | null,
  ) => void;
  onCurrencyChange?: (currency: Currency['currencyCode']) => void;
  onContactObjectChange?: (
    contactObject: {
      sourceResourceType: string;
      sourceRelationshipName: string;
    } | null,
  ) => void;
  isContactFieldsLoading?: boolean;
  isSyncConfigured?: boolean;
  onShowSyncModal?: () => void;
};

const IntegrationFieldId = {
  StartDate: 'startDate',
  Template: 'templateId',
};

const StyledMenu = styled(Menu)`
  box-shadow: var(--shadow-08);
`;

const StyledLoading = styled(Loading)`
  margin-left: 45%;
`;

const teamMembersSelectStyles = {
  valueContainer: (base) => ({
    ...base,
    maxHeight: 'calc(6 * var(--height-sm))',
    overflowY: 'auto',
    overflowX: 'hidden',
  }),
};

export const ActionDrawer: FC<React.PropsWithChildren<ActionDrawerProps>> = ({
  fields,
  fieldsStatus,
  visible,
  value,
  label,
  handleModalVisibility,
  onSave,
  onClose,
  placeholderFields,
  placeholderStatus,
  financialFields,
  isFinancialsFieldsFetching,
  onTemplateChange,
  onBillingMethodChange,
  onCurrencyChange,
  onContactObjectChange,
  isContactFieldsLoading,
  isSyncConfigured,
  onShowSyncModal,
}) => {
  const { automationDetails, setAutomationDetails } = useAutomationContext();
  const {
    salesforceObject: selectedSalesforceObject = { label: '', value: '' },
  } = automationDetails || {};

  const [localPlaceholder, setLocalPlaceholders] =
    useState<IntegrationField[]>();
  const [financialFieldsInfo, setFinancialFieldsInfo] =
    useState<IntegrationField[]>();

  const [searchedText, setSearchedText] = useState<string>('');
  const [hasContactObjectSelected, setHasContactObjectSelected] =
    useState(false);

  const queryClient = useQueryClient();
  const [rlFieldMappingForm] = Form.useForm<RLProjectFieldMapping>();

  useEffect(() => {
    if (placeholderStatus === 'success' && placeholderFields) {
      setLocalPlaceholders(placeholderFields);
    }
  }, [placeholderStatus, placeholderFields]);

  useEffect(() => {
    if (financialFields) {
      setFinancialFieldsInfo(financialFields);
    }
  }, [financialFields]);

  // Track if contact object is selected
  useEffect(() => {
    if (label === 'user' && visible) {
      const formValues = rlFieldMappingForm.getFieldsValue();

      setHasContactObjectSelected(
        !!formValues?.contactObject || !!value?.contactObject,
      );
    }
  }, [label, visible, rlFieldMappingForm, value]);

  useEffect(() => {
    // Reset form when drawer opens in create mode with no value
    // CRITICAL: Also reset if fields are loading to prevent showing stale cached data
    if (visible && !value) {
      // Clear all fields immediately
      const allFields = rlFieldMappingForm.getFieldsValue();
      const clearedFields = Object.keys(allFields).reduce((acc, key) => {
        acc[key] = undefined;

        return acc;
      }, {});

      rlFieldMappingForm.setFieldsValue(clearedFields);
      rlFieldMappingForm.resetFields();
    }
  }, [visible, value, rlFieldMappingForm, label, fieldsStatus]);

  // Populate form when values arrive after drawer has opened (async hydration case).
  useEffect(() => {
    if (visible && value) {
      rlFieldMappingForm.setFieldsValue(value);
    }
  }, [visible, value, rlFieldMappingForm]);

  // Reset form when company object or parent resource type changes in create mode
  useEffect(() => {
    if (visible && !value) {
      // Get all field names and set them to undefined explicitly
      const allFields = rlFieldMappingForm.getFieldsValue();
      const clearedFields = Object.keys(allFields).reduce((acc, key) => {
        acc[key] = undefined;

        return acc;
      }, {});

      // Explicitly set all fields to undefined, then reset
      rlFieldMappingForm.setFieldsValue(clearedFields);
      rlFieldMappingForm.resetFields();
    }
  }, [
    visible,
    value,
    automationDetails?.rocketlaneFields?.companyObject?.valueStr,
    automationDetails?.sourceParentResourceType,
    rlFieldMappingForm,
    label,
  ]);

  // CRITICAL FIX: Reset form when fields query status changes to pending
  // This prevents showing stale data while new fields are being fetched
  useEffect(() => {
    if (
      visible &&
      !value &&
      (fieldsStatus === 'pending' || fieldsStatus === true)
    ) {
      const allFields = rlFieldMappingForm.getFieldsValue();
      const clearedFields = Object.keys(allFields).reduce((acc, key) => {
        acc[key] = undefined;

        return acc;
      }, {});

      rlFieldMappingForm.setFieldsValue(clearedFields);
      rlFieldMappingForm.resetFields();
    }
  }, [visible, fieldsStatus, value, rlFieldMappingForm, label]);

  const handleSave = () => {
    rlFieldMappingForm
      .validateFields()
      .then((resp) => {
        // For contact drawer, check if sync is configured
        if (label === 'user' && !isSyncConfigured) {
          onShowSyncModal?.();

          return;
        }

        onSave(resp);

        queryClient.setQueriesData(
          {
            queryKey: [
              SALESFORCE_AUTOMATION_TEMPLATE_PLACEHOLDERS_FIELDS_QUERY_KEY,
            ],
          },
          localPlaceholder,
        );

        queryClient.setQueriesData(
          {
            queryKey: [
              SALESFORCE_AUTOMATION_BILLING_METHOD_FINANCIALS_FIELDS_QUERY_KEY,
            ],
          },
          financialFieldsInfo,
        );

        // Only close drawer if validation succeeds
        onClose?.();
      })
      .catch(() => {});
  };

  const clearFieldValues = (fieldsToClear?: IntegrationField[]) => {
    const idsToClear = (fieldsToClear ?? [])
      .map((field) => field.integrationFieldId)
      .filter(Boolean);

    if (idsToClear.length === 0) {
      return;
    }

    const clearedValues = idsToClear.reduce((acc, fieldId) => {
      acc[fieldId] = undefined;

      return acc;
    }, {});

    rlFieldMappingForm.setFieldsValue(clearedValues);
    rlFieldMappingForm.resetFields(idsToClear);
  };

  const handleSelectTemplate = ({ templateId }) => {
    if (templateId) {
      onTemplateChange?.(templateId.valueStr);
    }
    if (templateId === null) {
      clearFieldValues(localPlaceholder);
      setLocalPlaceholders([]);
      onTemplateChange?.(null);
    }
  };

  const handleBillingMethodChange = ({ financialContractType }) => {
    if (financialContractType) {
      onBillingMethodChange?.(financialContractType.valueStr);
      clearFieldValues(financialFieldsInfo);
      setFinancialFieldsInfo([]);
      rlFieldMappingForm.setFieldsValue({ rateCardId: undefined });
      rlFieldMappingForm.validateFields(['rateCardId']);
    }
    if (financialContractType === null) {
      clearFieldValues(financialFieldsInfo);
      setFinancialFieldsInfo([]);
      onBillingMethodChange?.(null);
    }
  };

  const handleCurrencyChange = ({ currency: _currency }) => {
    if (_currency) {
      onCurrencyChange?.(_currency.valueStr);
      clearFieldValues(financialFieldsInfo);
      setFinancialFieldsInfo([]);
    }
  };

  const handleContactObjectChangeInternal = ({ contactObject }) => {
    if (contactObject?.valueMeta) {
      // Preserve selection in context immediately so it doesn't get lost on refetch/reset
      // (e.g. when user goes to Field Sync and comes back).
      if (label === 'user') {
        setAutomationDetails({
          contactFields: {
            ...(automationDetails?.contactFields ?? {}),
            contactObject,
          } as any,
        });
      }
      onContactObjectChange?.({
        sourceResourceType: contactObject.valueMeta.sourceResourceType,
        sourceRelationshipName: contactObject.valueMeta.sourceRelationshipName,
      });
    }
    if (contactObject === null) {
      if (label === 'user') {
        setAutomationDetails({
          contactFields: undefined,
        });
      }
      onContactObjectChange?.(null);
    }
  };

  const requiredText = useTranslation('input.required');
  const excludeSquareBrackFields = ['templateId'];
  const rocketlaneText = useTranslation('rocketlane' as any);
  const renderedFieldIds = new Set<string>();
  const getUniqueFields = (fieldList?: IntegrationField[]) =>
    (fieldList ?? []).filter((field) => {
      const fieldId = field.integrationFieldId;

      if (!fieldId || renderedFieldIds.has(fieldId)) {
        return false;
      }

      renderedFieldIds.add(fieldId);

      return true;
    });

  const uniqueBaseFields = getUniqueFields(fields);
  const uniquePlaceholderFields =
    placeholderStatus === 'pending' ? [] : getUniqueFields(localPlaceholder);
  const uniqueFinancialFields = isFinancialsFieldsFetching
    ? []
    : getUniqueFields(financialFields);
  const scrollTeamMembersSelectionIntoView = () => {
    window.requestAnimationFrame(() => {
      const containers = document.querySelectorAll(
        '.team-members-select .ozone-select__value-container',
      );
      const latestContainer = containers[containers.length - 1] as
        | HTMLElement
        | undefined;

      if (latestContainer) {
        latestContainer.scrollTop = latestContainer.scrollHeight;
      }
    });
  };

  const getOptionsInFormat = (field) => {
    const tempObj = groupBy(
      field.metaData?.options,
      'valueMeta.parentFieldName',
    );

    let groupedOptions: any[] = Object.keys(tempObj).map((key) => {
      const selectedAccountObjectHeading =
        label === 'user' && field?.integrationFieldId === 'contactObject'
          ? (automationDetails?.rocketlaneFields as any)?.companyObject?.label
          : undefined;

      const groupLabel =
        field.fieldName === 'templateId'
          ? rocketlaneText
          : key !== 'undefined'
            ? key
            : selectedAccountObjectHeading ||
              selectedSalesforceObject?.label ||
              '';

      return {
        label: `${groupLabel}`,
        options: [
          ...tempObj[key].map((option) => {
            const optionValue = option?.valueStr ?? option?.value;

            return {
              ...option,
              label: excludeSquareBrackFields.includes(field.fieldName)
                ? option.label
                : option.label.indexOf(']') > -1
                  ? option.label.split(']')[1].trim()
                  : option.label,
              value: optionValue,
              valueStr:
                option?.valueStr ??
                (optionValue != null ? String(optionValue) : option?.valueStr),
            };
          }),
        ],
      };
    });

    const nativeFields: any = groupedOptions.find((option) =>
      option.label.startsWith(selectedSalesforceObject?.label || ''),
    );

    const rocketLaneFields: any = groupedOptions.find((option) =>
      option.label.toLowerCase().startsWith(rocketlaneText.toLowerCase()),
    );

    if (rocketLaneFields !== undefined && nativeFields !== undefined) {
      groupedOptions = [
        rocketLaneFields,
        nativeFields,
        ...groupedOptions.filter(
          (option) =>
            !option.label.startsWith(selectedSalesforceObject?.label || '') &&
            !option.label
              .toLowerCase()
              .startsWith(rocketlaneText.toLowerCase()),
        ),
      ];
    } else if (nativeFields !== undefined) {
      groupedOptions = [
        nativeFields,
        ...groupedOptions.filter(
          (option) =>
            !option.label.startsWith(selectedSalesforceObject?.label || ''),
        ),
      ];
    }

    return groupedOptions;
  };

  return (
    <CustomLayoutDrawer
      {...DefaultCustomLayoutDrawerProps}
      open={visible}
      width={500}
      onClose={onClose}
    >
      <CustomLayoutDrawerContent>
        <CustomLayoutDrawerContent.Title>
          <FormattedMessage
            id={`settings.integrations.create.automation.${label?.toLowerCase()}.label`}
          />
        </CustomLayoutDrawerContent.Title>
        <CustomLayoutDrawerContent.Content>
          <>
            {label === 'user' &&
              hasContactObjectSelected &&
              !isContactFieldsLoading &&
              !isSyncConfigured && (
                <InlineNotification
                  msg={
                    <span style={{ width: '100%', display: 'block' }}>
                      Configure{' '}
                      <strong>{automationDetails?.sourceResourceType}</strong>{' '}
                      field mapping in the field sync menu.
                    </span>
                  }
                  type={InlineNotificationType.Info}
                  icon={<FontAwesomeIcon icon={faCircleInfo} />}
                />
              )}
            {fieldsStatus === 'pending' || fieldsStatus === true ? (
              <Loading />
            ) : (
              <Row justify="space-around" align="middle">
                <Col span={24}>
                  <Form
                    onValuesChange={(e) => {
                      handleSelectTemplate(e);
                      handleBillingMethodChange(e);
                      handleCurrencyChange(e);
                      handleContactObjectChangeInternal(e);

                      // Track contact object selection
                      if (label === 'user' && e.contactObject !== undefined) {
                        setHasContactObjectSelected(!!e.contactObject);
                      }
                    }}
                    layout="vertical"
                    form={rlFieldMappingForm}
                    autoComplete="off"
                    style={{ marginTop: 'var(--spacing-05)' }}
                    initialValues={value}
                  >
                    {uniqueBaseFields.map((field) => {
                      const isContactObjectField =
                        field.integrationFieldId === 'contactObject';

                      return (
                        <>
                          <Form.Item
                            name={field.integrationFieldId}
                            key={field.integrationFieldId}
                            label={field.fieldLabel}
                            rules={[
                              {
                                required: field?.required,
                                message: requiredText,
                              },
                              ({ getFieldValue }) => ({
                                validator(_, fieldValue) {
                                  // if template is selected, start date is requried
                                  if (
                                    field.integrationFieldId ===
                                      IntegrationFieldId.StartDate &&
                                    !fieldValue &&
                                    getFieldValue(IntegrationFieldId.Template)
                                  ) {
                                    return Promise.reject(requiredText);
                                  }

                                  return Promise.resolve();
                                },
                              }),
                            ]}
                            dependencies={
                              field.integrationFieldId ===
                              IntegrationFieldId.StartDate
                                ? [IntegrationFieldId.Template]
                                : undefined
                            }
                          >
                            <Select
                              className={
                                field.fieldName === 'teamMembers'
                                  ? 'team-members-select'
                                  : undefined
                              }
                              name={field.integrationFieldId}
                              isMulti={field.fieldName === 'teamMembers'}
                              styles={
                                field.fieldName === 'teamMembers'
                                  ? teamMembersSelectStyles
                                  : undefined
                              }
                              onChange={
                                field.fieldName === 'teamMembers'
                                  ? () => scrollTeamMembersSelectionIntoView()
                                  : undefined
                              }
                              components={{
                                IndicatorSeparator: null,
                                Control,
                                Menu: StyledMenu,
                                MenuList: VirtualizedMenuList,
                                SingleValue: SelectedSingleValue,
                                MultiValueLabel: SelectedMultiValueLabel,
                              }}
                              menuPlacement="auto"
                              formatOptionLabel={DisplayDynamicValue}
                              isClearable
                              hideSelectedOptions={
                                field.fieldName === 'teamMembers'
                              }
                              closeMenuOnSelect={
                                field.fieldName !== 'teamMembers'
                              }
                              tabSelectsValue={false}
                              options={getOptionsInFormat(field)}
                              grouped
                              searchedText={searchedText}
                              onInputChange={(input) => setSearchedText(input)}
                            />
                          </Form.Item>
                          {isContactObjectField && isContactFieldsLoading && (
                            <StyledLoading />
                          )}
                        </>
                      );
                    })}
                    {placeholderStatus === 'pending' ? (
                      <>{localPlaceholder && <StyledLoading />}</>
                    ) : (
                      uniquePlaceholderFields.map((field) => (
                        <Form.Item
                          name={field.integrationFieldId}
                          key={field.integrationFieldId}
                          label={field.fieldLabel}
                          rules={[
                            {
                              required: field?.required,
                              message: requiredText,
                            },
                            ({ getFieldValue }) => ({
                              validator(_, fieldValue) {
                                // if template is selected, start date is requried
                                if (
                                  field.integrationFieldId ===
                                    IntegrationFieldId.StartDate &&
                                  !fieldValue &&
                                  getFieldValue(IntegrationFieldId.Template)
                                ) {
                                  return Promise.reject(requiredText);
                                }

                                return Promise.resolve();
                              },
                            }),
                          ]}
                        >
                          <Select
                            className={
                              field.fieldName === 'teamMembers'
                                ? 'team-members-select'
                                : undefined
                            }
                            name={field.integrationFieldId}
                            styles={
                              field.fieldName === 'teamMembers'
                                ? teamMembersSelectStyles
                                : undefined
                            }
                            onChange={
                              field.fieldName === 'teamMembers'
                                ? () => scrollTeamMembersSelectionIntoView()
                                : undefined
                            }
                            components={{
                              IndicatorSeparator: null,
                              Control,
                              Menu: StyledMenu,
                              MenuList: VirtualizedMenuList,
                              SingleValue: SelectedSingleValue,
                              MultiValueLabel: SelectedMultiValueLabel,
                            }}
                            menuPlacement="auto"
                            formatOptionLabel={DisplayDynamicValue}
                            isClearable
                            hideSelectedOptions={
                              field.fieldName === 'teamMembers'
                            }
                            closeMenuOnSelect={
                              field.fieldName !== 'teamMembers'
                            }
                            tabSelectsValue={false}
                            options={getOptionsInFormat(field)}
                            grouped
                            searchedText={searchedText}
                            onInputChange={(input) => setSearchedText(input)}
                          />
                        </Form.Item>
                      ))
                    )}
                    {isFinancialsFieldsFetching ? (
                      <StyledLoading />
                    ) : (
                      uniqueFinancialFields.map((field) => (
                        <Form.Item
                          name={field.integrationFieldId}
                          key={field.integrationFieldId}
                          label={field.fieldLabel}
                          rules={[
                            {
                              required: field?.required,
                              message: requiredText,
                            },
                          ]}
                          shouldUpdate
                        >
                          <Select
                            className={
                              field.fieldName === 'teamMembers'
                                ? 'team-members-select'
                                : undefined
                            }
                            name={field.integrationFieldId}
                            styles={
                              field.fieldName === 'teamMembers'
                                ? teamMembersSelectStyles
                                : undefined
                            }
                            onChange={
                              field.fieldName === 'teamMembers'
                                ? () => scrollTeamMembersSelectionIntoView()
                                : undefined
                            }
                            components={{
                              IndicatorSeparator: null,
                              Control,
                              Menu: StyledMenu,
                              MenuList: VirtualizedMenuList,
                              SingleValue: SelectedSingleValue,
                              MultiValueLabel: SelectedMultiValueLabel,
                            }}
                            menuPlacement="auto"
                            formatOptionLabel={DisplayDynamicValue}
                            isClearable
                            hideSelectedOptions={
                              field.fieldName === 'teamMembers'
                            }
                            closeMenuOnSelect={
                              field.fieldName !== 'teamMembers'
                            }
                            tabSelectsValue={false}
                            options={getOptionsInFormat(field)}
                            grouped
                            searchedText={searchedText}
                            onInputChange={(input) => setSearchedText(input)}
                          />
                        </Form.Item>
                      ))
                    )}
                  </Form>
                </Col>
              </Row>
            )}
          </>
        </CustomLayoutDrawerContent.Content>
        <CustomLayoutDrawerContent.Footer>
          <ButtonGroup $reversed>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={
                label === 'user' &&
                (!hasContactObjectSelected || isContactFieldsLoading)
              }
            >
              {label === 'user' ? (
                isContactFieldsLoading ||
                isSyncConfigured ||
                !hasContactObjectSelected ? (
                  <FormattedMessage id="done" />
                ) : (
                  'Sync Field now'
                )
              ) : (
                <FormattedMessage id="save" />
              )}
            </Button>
            <Button
              variant="secondary"
              onClick={() => handleModalVisibility(false)}
            >
              <FormattedMessage id="cancel" />
            </Button>
          </ButtonGroup>
        </CustomLayoutDrawerContent.Footer>
      </CustomLayoutDrawerContent>
    </CustomLayoutDrawer>
  );
};
